/**
 * O que a RODA DO MOUSE faz no quadro branco — zoom, pan lateral ou pan vertical.
 *
 * ─── por que este arquivo existe separado ───────────────────────────────────────────────────
 *
 * As mesmas duas razões do irmão `blocks/surface/src/tuninho/decidir-pan.ts`, e a segunda é a
 * que decide.
 *
 * 1. `edgeless-root-block.ts` é arquivo do upstream, e tudo que se escreve lá vira patch a
 *    manter a cada atualização do Affine (Regra 8.15 — *arquivo novo não conflita; arquivo
 *    editado conflita*). O que sobra lá é a fiação; a decisão mora aqui.
 *
 * 2. **Este arquivo não importa NADA.** Sem `@blocksuite`, sem `WheelEvent`, sem `IS_WINDOWS`.
 *    É isso que permite `cli/tests/js/roda-do-edgeless.test.ts` executá-lo de verdade nesta
 *    estação, sem `node_modules` do Affine e sem o build que a Regra 8.7.3 cataloga.
 *
 *    ⚠️ No dia em que precisar importar de `@blocksuite`, ele deixou de ser puro e o teste
 *    para de carregar. Isso é o gate, não um acidente.
 *
 * ─── o que estava aqui antes, e o que foi medido ────────────────────────────────────────────
 *
 * Medido no `dev` em 2026-09-14, com o quadro aberto e o zoom lido do próprio viewport
 * (`gfx.viewport.zoom`), com `edgelessScrollZoom` no default (desligado):
 *
 *     scroll puro         pan vertical          ✅ era o esperado
 *     Ctrl  + scroll      zoom  0.65 → 0.55     ✅ já funcionava
 *     Cmd   + scroll      pan vertical          ❌ só zooma em Mac/iOS
 *     Shift + scroll      pan VERTICAL          ❌ deveria ser horizontal
 *     Alt   + scroll      pan vertical          ❌ não fazia nada de especial
 *
 * As duas últimas linhas são o trabalho. A terceira é o buraco que o card nomeia; a quarta é
 * a que ninguém tinha notado — o *"scroll lateral que já temos"* do card **não existia** fora
 * do Windows, porque o upstream condiciona a conversão a `IS_WINDOWS && e.shiftKey`.
 *
 * ─── por que o Shift NÃO entra no zoom ──────────────────────────────────────────────────────
 *
 * O card pede zoom com *"uma tecla como o shift ou cmmd ou control"*. Shift é a única das três
 * que **já tem dono**: é o pan horizontal do Figma, do FigJam, do Miro e do próprio navegador.
 * E é exatamente a *"movimentacao do espaco pelo scroll lateral"* que o mesmo card pede para
 * manter.
 *
 * Vale aqui o princípio que a operação do arraste no vazio (card 1850942686123525322) elevou a
 * regra: **o gesto que estou tomando estava fazendo alguma coisa? Se estava, trocar sem
 * devolver não é mudança — é remoção.** Dar zoom ao Shift custaria o pan lateral; Ctrl, Cmd e
 * Alt entregam o zoom sem custo nenhum.
 */

/** O que fazer com um giro da roda. Três respostas, nunca duas (Regra 8.14). */
export type DecisaoDaRoda = 'zoom' | 'pan-horizontal' | 'pan';

export interface GestoDeRoda {
  /**
   * `Ctrl`. ⚠️ Vem `true` também na **pinça de dois dedos do trackpad**, sem ninguém tocar a
   * tecla — o Chromium codifica a pinça assim desde 2015 (bug 397027). É por isso que o zoom
   * por Ctrl já funcionava antes deste arquivo existir: ninguém o escreveu pensando em
   * teclado.
   */
  comCtrl: boolean;
  /** `Cmd`/`Meta`. O upstream só o consulta em Mac e iOS; aqui vale em toda plataforma. */
  comMeta: boolean;
  /** `Alt`. Estava livre — não é atalho de roda em ferramenta nenhuma do mercado. */
  comAlt: boolean;
  /** `Shift`. Pan horizontal, nunca zoom — ver o cabeçalho. */
  comShift: boolean;
  /**
   * O ajuste `edgelessScrollZoom` do upstream. `undefined` = o sinal de configuração ainda não
   * emitiu (ele nasce `{}` em `createSignalFromObservable`) e **não** significa ligado — é o
   * mesmo `?? false` que o upstream já fazia.
   */
  zoomComRodaNua: boolean | undefined;
}

/**
 * Decide o que um giro da roda faz.
 *
 * A ordem dos testes é o contrato, e cada linha existe por um motivo:
 */
export function decidirRoda({
  comCtrl,
  comMeta,
  comAlt,
  comShift,
  zoomComRodaNua,
}: GestoDeRoda): DecisaoDaRoda {
  // 1. Com o ajuste LIGADO, o upstream dá zoom em tudo — inclusive com modificador, porque a
  //    condição dele é `isTouchPadPinchEvent(e) || edgelessScrollZoom`. Preservado à risca:
  //    o card fala do caso DESLIGADO, e o que não foi pedido não se muda.
  if (zoomComRodaNua === true) return 'zoom';

  // 2. As três teclas de zoom. `Ctrl` é a que já funcionava (e carrega a pinça do trackpad
  //    junto); `Cmd` e `Alt` são o que este card acrescenta.
  if (comCtrl || comMeta || comAlt) return 'zoom';

  // 3. `Shift` é pan horizontal — a convenção de mercado, e o que o card chama de "scroll
  //    lateral". Vem DEPOIS do zoom de propósito: `Ctrl+Shift+roda` dá zoom, que é o que
  //    qualquer pessoa espera de uma combinação em que uma das teclas é a de zoom.
  if (comShift) return 'pan-horizontal';

  // 4. O destino seguro é o comportamento de sempre.
  return 'pan';
}

/** O deslocamento do quadro, em unidades de tela (o chamador ainda divide pelo zoom). */
export interface DeslocamentoDaRoda {
  dx: number;
  dy: number;
}

/**
 * Converte o giro em deslocamento.
 *
 * ⚠️ **O eixo em que o valor chega não é o mesmo em toda plataforma**, e este é o fato do
 * outro lado que nenhum tipo declara (Regra 8.48.1). Com `Shift` segurado, **o macOS já
 * entrega o giro em `deltaX`** — o sistema converte antes de o evento chegar ao JavaScript —
 * enquanto Windows e Linux entregam em `deltaY` e esperam que a aplicação converta. Ler só
 * `deltaY` deixaria o Mac parado; ler só `deltaX` deixaria o resto parado.
 *
 * A saída é não escolher: no pan horizontal, o valor é o que **tiver chegado**. Quando o
 * sistema já converteu, `deltaX` traz o número e `deltaY` vem zero; quando não converteu, o
 * inverso. Um `?? 0` não serviria — os dois campos sempre existem; o que varia é qual é zero.
 */
export function deslocamentoDaRoda(
  decisao: DecisaoDaRoda,
  deltaX: number,
  deltaY: number
): DeslocamentoDaRoda {
  if (decisao === 'pan-horizontal') {
    return { dx: deltaX !== 0 ? deltaX : deltaY, dy: 0 };
  }
  return { dx: deltaX, dy: deltaY };
}

/**
 * ─── a pinça de dois dedos no SAFARI ────────────────────────────────────────────────────────
 *
 * Todo navegador **menos o Safari** codifica a pinça do trackpad como um evento de roda com
 * `ctrlKey` ligado — foi assim que o Chromium resolveu em 2015 (bug 397027) e virou padrão de
 * fato. É por isso que a pinça já dava zoom aqui sem ninguém ter escrito nada para ela.
 *
 * O Safari não seguiu. Ele dispara `gesturestart` / `gesturechange` / `gestureend`, uma API
 * própria da Apple, e **não manda `wheel` nenhum**. Medido na árvore do Mirezi: o upstream
 * não escuta esses eventos em lugar algum, então no Safari — no Mac e no iPad — a pinça de
 * dois dedos caía no chão. Não é defeito do aparelho; é um gesto que o produto nunca ouviu.
 *
 * O `scale` do `GestureEvent` é **acumulado desde o `gesturestart`**, não incremental: ele
 * vale 1 no começo e cresce ou encolhe a partir dali. Por isso o zoom se calcula sobre o valor
 * que o viewport tinha quando o gesto COMEÇOU — multiplicar pelo zoom corrente a cada evento
 * elevaria a escala a cada quadro, e um beliscão viraria um salto.
 */

/**
 * O zoom que uma pinça do Safari produz.
 *
 * `escalaAcumulada` é o `GestureEvent.scale`; `zoomNoInicioDoGesto` é o zoom lido no
 * `gesturestart`. O clamp aos limites do canvas é do `viewport.setZoom`, que já o faz — aqui
 * só se guarda contra o que não é número, porque um `NaN` chegaria ao viewport e apagaria o
 * quadro da tela sem erro nenhum.
 */
export function zoomDaPinca(
  escalaAcumulada: number,
  zoomNoInicioDoGesto: number
): number {
  if (!Number.isFinite(escalaAcumulada) || escalaAcumulada <= 0) {
    return zoomNoInicioDoGesto;
  }
  if (!Number.isFinite(zoomNoInicioDoGesto) || zoomNoInicioDoGesto <= 0) {
    return zoomNoInicioDoGesto;
  }
  return zoomNoInicioDoGesto * escalaAcumulada;
}

/** O retângulo do quadro na tela, como `getBoundingClientRect()` o devolve. */
export interface RetanguloNaTela {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/**
 * O gesto começou DENTRO do quadro?
 *
 * Existe porque o listener de gesto do Safari mora no `document`, e não neste componente — o
 * `GestureEvent` é proprietário e nada garante que ele seja `composed`, então um listener no
 * host do shadow DOM pode simplesmente nunca vê-lo. No `document` ele chega sempre, e o preço
 * é ter de decidir se o gesto é nosso. A decisão é por **geometria**, nunca pelo `target`: no
 * shadow DOM o alvo é retargetado para o host e diria "é seu" para um gesto sobre qualquer
 * coisa dentro dele.
 *
 * ⚠️ **Retângulo de área zero devolve `false`.** É o que acontece com a aba em segundo plano ou
 * com o componente desmontando — e um `0,0,0,0` faria a comparação de bordas dar verdadeiro
 * para o ponto `(0,0)`, roubando a pinça do canto superior esquerdo da página. Falhar para o
 * lado de "não é nosso" é o destino seguro: o pior caso é o navegador fazer o que sempre fez.
 */
export function oGestoEstaNoQuadro(
  x: number,
  y: number,
  r: RetanguloNaTela
): boolean {
  if (!(r.width > 0) || !(r.height > 0)) return false;
  return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
}

/**
 * ─── a tecla que o Safari do iPad NÃO carimba no evento de roda ─────────────────────────────
 *
 * Medido no iPad do operador, com a sonda guiada, em três rodadas:
 *
 *     Cmd   + rolar   310 eventos de roda, `metaKey`  em ZERO deles   · teclado anunciou ⌘
 *     Ctrl  + rolar   146 eventos de roda, `ctrlKey`  em ZERO deles   · teclado anunciou ⌃
 *     Shift + rolar   191 eventos de roda, `shiftKey` em ZERO deles   · teclado anunciou ⇧
 *     pinça            NENHUM evento — o iPadOS consome o gesto inteiro
 *
 * **`wheel` e `keydown` são canais diferentes**, e no iPadOS só o segundo carrega a tecla. Sem
 * isto, `Cmd + rolar` no trackpad do iPad é inalcançável: não há evento a que reagir.
 *
 * O estado vive aqui, e a roda passa a perguntar *"o evento trouxe a tecla **ou** o teclado a
 * anunciou?"*. No desktop nada muda — lá `e.metaKey` já vem preenchido e ganha sozinho.
 *
 * ⚠️ **O risco deste desenho é a tecla FICAR PRESA.** Um `keyup` que não chega — a janela perde
 * o foco com a tecla apertada, o gesto termina fora da página — deixaria o quadro dando zoom a
 * cada rolagem, para sempre, sem nada falhar. Seria pior que o defeito que isto conserta. Três
 * defesas, e a primeira é a que carrega o peso:
 *
 * 1. **todo evento de teclado reconcilia o estado inteiro** a partir das flags que ele traz
 *    (`metaKey`, `ctrlKey`…). Elas descrevem o estado do teclado *naquele instante*, então
 *    qualquer tecla digitada depois — inclusive uma letra qualquer — conserta o que ficou
 *    preso. É correção contínua, não um `keyup` que precisa chegar;
 * 2. `esqueceAsTeclas()` no `blur` e ao esconder a aba — os dois momentos em que o `keyup`
 *    comprovadamente se perde;
 * 3. o estado só ADICIONA à decisão (`evento || rastreado`), nunca remove: uma tecla presa no
 *    rastreio não é capaz de desligar um zoom que o evento pediu.
 */

/** O que o teclado anunciou estar pressionado. */
export interface EstadoDasTeclas {
  meta: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
}

/** Nenhuma tecla pressionada — o estado inicial, e o destino de toda defesa. */
export function esqueceAsTeclas(): EstadoDasTeclas {
  return { meta: false, ctrl: false, alt: false, shift: false };
}

/** As quatro modificadoras, na grafia de `KeyboardEvent.key`. */
const MODIFICADORAS: Record<string, keyof EstadoDasTeclas> = {
  Meta: 'meta',
  Control: 'ctrl',
  Alt: 'alt',
  Shift: 'shift',
};

export interface FlagsDeTeclado {
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * O estado novo depois de um evento de teclado.
 *
 * Começa pelas **flags do próprio evento** — elas são a verdade do instante, e é isso que faz
 * uma tecla presa se consertar sozinha na próxima tecla digitada. Depois disso, e só depois, a
 * tecla NOMEADA pelo evento é aplicada: num `keyup` de `Meta` o `metaKey` de alguns navegadores
 * ainda vem `true` (o estado é lido antes da soltura), e sem esta linha o ⌘ ficaria preso
 * exatamente no evento que existe para soltá-lo.
 */
export function aplicaEventoDeTeclado(
  tipo: 'keydown' | 'keyup',
  key: string,
  flags: FlagsDeTeclado
): EstadoDasTeclas {
  const estado: EstadoDasTeclas = {
    meta: !!flags.metaKey,
    ctrl: !!flags.ctrlKey,
    alt: !!flags.altKey,
    shift: !!flags.shiftKey,
  };
  const qual = MODIFICADORAS[key];
  if (qual) estado[qual] = tipo === 'keydown';
  return estado;
}

/**
 * As teclas que valem para decidir a roda.
 *
 * `evento || rastreado`, nesta ordem e nunca o contrário: onde o navegador carimba a tecla
 * (todo desktop), ele manda; onde não carimba (o trackpad do iPad), entra o que o teclado
 * anunciou. Um `||` também garante que o rastreio só saiba **acrescentar** — ver a 3ª defesa
 * no cabeçalho.
 */
export function teclasDaRoda(
  doEvento: FlagsDeTeclado,
  rastreado: EstadoDasTeclas
): Pick<GestoDeRoda, 'comCtrl' | 'comMeta' | 'comAlt' | 'comShift'> {
  return {
    comCtrl: !!doEvento.ctrlKey || rastreado.ctrl,
    comMeta: !!doEvento.metaKey || rastreado.meta,
    comAlt: !!doEvento.altKey || rastreado.alt,
    comShift: !!doEvento.shiftKey || rastreado.shift,
  };
}
